import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { UserCheck, Eye, CheckCircle, XCircle, TrendingUp, Users, Euro, Power, Trash2, AlertTriangle, Edit, X, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import PayoutsManagement from './PayoutsManagement';

const VendorsManagement = () => {
  const [activeTab, setActiveTab] = useState('vendors');
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showBalanceModal, setShowBalanceModal] = useState(null);
  const [showCommissionModal, setShowCommissionModal] = useState(null); // NOUVEAU
  const [balanceFields, setBalanceFields] = useState({
    balance_available: '',
    balance_in_withdrawal: '',
    balance_pending: ''
  });
  const [balanceReason, setBalanceReason] = useState('');
  const [newCommission, setNewCommission] = useState(''); // NOUVEAU
  const [commissionReason, setCommissionReason] = useState(''); // NOUVEAU
  const [vendorClients, setVendorClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [balanceSuccess, setBalanceSuccess] = useState('');
  const [commissionLoading, setCommissionLoading] = useState(false); // NOUVEAU
  const [commissionSuccess, setCommissionSuccess] = useState(''); // NOUVEAU

  useEffect(() => {
    fetchVendors();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await api.get('/admin/vendors');
      setVendors(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const approveVendor = async (vendorId) => {
    setActionLoading(vendorId);
    try {
      await api.put(`/admin/vendors/${vendorId}/approve`);
      fetchVendors();
    } catch (error) {
      console.error('Error approving vendor:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleVendorStatus = async (vendorId) => {
    setActionLoading(vendorId);
    try {
      await api.put(`/admin/vendors/${vendorId}/toggle-status`);
      fetchVendors();
    } catch (error) {
      console.error('Error toggling vendor status:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteVendor = async (vendorId) => {
    setActionLoading(vendorId);
    try {
      await api.delete(`/admin/vendors/${vendorId}`);
      setShowDeleteConfirm(null);
      fetchVendors();
    } catch (error) {
      console.error('Error deleting vendor:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const openBalanceModal = (vendor) => {
    setShowBalanceModal(vendor);
    setBalanceFields({
      balance_available: (vendor.balance_available ?? 0).toString(),
      balance_in_withdrawal: (vendor.balance_in_withdrawal ?? 0).toString(),
      balance_pending: (vendor.balance_pending ?? 0).toString()
    });
    setBalanceReason('');
    setBalanceSuccess('');
  };

  const updateVendorBalance = async () => {
    if (!showBalanceModal) return;
    
    setBalanceLoading(true);
    try {
      const payload = {
        reason: balanceReason || 'Modification admin'
      };

      const availableValue = parseFloat(balanceFields.balance_available);
      const inWithdrawalValue = parseFloat(balanceFields.balance_in_withdrawal);
      const pendingValue = parseFloat(balanceFields.balance_pending);

      if (!isNaN(availableValue)) {
        payload.balance_available = availableValue;
      }
      if (!isNaN(inWithdrawalValue)) {
        payload.balance_in_withdrawal = inWithdrawalValue;
      }
      if (!isNaN(pendingValue)) {
        payload.balance_pending = pendingValue;
      }

      await api.put(`/admin/vendors/${showBalanceModal.id}/balance`, payload);
      setBalanceSuccess('Solde mis à jour !');
      fetchVendors();
      setTimeout(() => {
        setShowBalanceModal(null);
        setBalanceSuccess('');
      }, 1500);
    } catch (error) {
      console.error('Error updating balance:', error);
    } finally {
      setBalanceLoading(false);
    }
  };

  const viewVendorClients = async (vendor) => {
    setSelectedVendor(vendor);
    try {
      const response = await api.get(`/admin/vendor/${vendor.id}/clients`);
      setVendorClients(response.data || []);
    } catch {
      setVendorClients([]);
    }
    setShowDetails(true);
  };

  const getCommissionTier = (salesCount) => {
    // Paliers avec commissions fixes en euros
    if (salesCount >= 40) return { amount: 25, tier: 'Palier 4 (40+)', color: 'text-[#FFB800]' };
    if (salesCount >= 20) return { amount: 20, tier: 'Palier 3 (20-39)', color: 'text-[#00FF88]' };
    if (salesCount >= 10) return { amount: 15, tier: 'Palier 2 (10-19)', color: 'text-[#00FFFF]' };
    return { amount: 10, tier: 'Palier 1 (0-9)', color: 'text-[#B8B8BA]' };
  };

  const openCommissionModal = (vendor) => {
    const tier = getCommissionTier(vendor.sales_count || 0);
    setShowCommissionModal(vendor);
    setNewCommission(tier.amount.toString());
    setCommissionReason('');
    setCommissionSuccess('');
  };

  const updateVendorCommission = async () => {
    if (!showCommissionModal) return;
    
    const commission = parseFloat(newCommission);
    if (isNaN(commission) || commission < 0) {
      alert('Montant invalide');
      return;
    }

    setCommissionLoading(true);
    try {
      await api.put(`/admin/vendors/${showCommissionModal.id}/commission`, {
        commission_rate: commission,
        reason: commissionReason
      });
      setCommissionSuccess('Commission mise à jour avec succès !');
      setTimeout(() => {
        setShowCommissionModal(null);
        fetchVendors();
        if (selectedVendor && selectedVendor.id === showCommissionModal.id) {
          viewVendorClients(showCommissionModal);
        }
      }, 1500);
    } catch (error) {
      alert('Erreur lors de la mise à jour de la commission');
      console.error('Error updating commission:', error);
    } finally {
      setCommissionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <div className="p-8" data-testid="vendors-management">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Gestion Vendeurs</h1>
        <p className="text-[#B8B8BA] text-sm">Administration du programme vendeur</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[#121214] p-1 rounded-lg border border-[#3A3A3D]/40 w-fit" data-testid="vendor-tabs">
        <button onClick={() => setActiveTab('vendors')}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all ${activeTab === 'vendors' ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D]'}`} data-testid="tab-vendors">
          <UserCheck className="h-4 w-4" />Vendeurs
        </button>
        <button onClick={() => setActiveTab('payouts')}
          className={`flex items-center gap-2 px-4 py-2 rounded text-sm font-medium transition-all ${activeTab === 'payouts' ? 'bg-[#FF6B35] text-black' : 'text-[#B8B8BA] hover:text-white hover:bg-[#1A1A1D]'}`} data-testid="tab-vendor-payouts">
          <Euro className="h-4 w-4" />Paiements Vendeurs
        </button>
      </div>

      {activeTab === 'payouts' ? (
        <PayoutsManagement />
      ) : (
      <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA]">Vendeurs actifs</span>
            <UserCheck className="h-5 w-5 text-[#00FF88]" />
          </div>
          <div className="text-3xl font-bold text-white">{vendors.filter(v => v.is_approved).length}</div>
        </div>
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA]">En attente</span>
            <Users className="h-5 w-5 text-[#FFB800]" />
          </div>
          <div className="text-3xl font-bold text-[#FFB800]">{vendors.filter(v => !v.is_approved).length}</div>
        </div>
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA]">Ventes totales</span>
            <TrendingUp className="h-5 w-5 text-[#00FFFF]" />
          </div>
          <div className="text-3xl font-bold text-white">{vendors.reduce((acc, v) => acc + (v.sales_count || 0), 0)}</div>
        </div>
        <div className="bg-[#1A1A1D] p-6 rounded-lg metallic-border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[#B8B8BA]">Commissions dues</span>
            <Euro className="h-5 w-5 text-[#00FFFF]" />
          </div>
          <div className="text-3xl font-bold text-white">{vendors.reduce((acc, v) => acc + (v.balance || 0) + (v.pending_balance || 0), 0).toFixed(0)}€</div>
        </div>
      </div>

      <div className="bg-[#1A1A1D] rounded-lg metallic-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#222226]">
            <tr>
              <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Vendeur</th>
              <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Code</th>
              <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Ventes</th>
              <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Commission</th>
              <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Solde Dispo</th>
              <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">En cours de retrait</th>
              <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Solde en attente</th>
              <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Statut</th>
              <th className="px-6 py-4 text-left text-[#B8B8BA] font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map((vendor, index) => (
              <tr key={vendor.id} className="border-t border-[#3A3A3D] hover:bg-[#222226] transition-colors" data-testid={`vendor-row-${index}`}>
                <td className="px-6 py-4">
                  <div className="text-white font-semibold">{vendor.full_name}</div>
                  <div className="text-[#B8B8BA] text-xs">{vendor.email}</div>
                </td>
                <td className="px-6 py-4 text-[#00FFFF] font-mono font-bold">{vendor.vendor_code}</td>
                <td className="px-6 py-4 text-white font-semibold">{vendor.sales_count || 0}</td>
                <td className="px-6 py-4">
                  <span className={`font-bold ${getCommissionTier(vendor.sales_count || 0).color}`}>
                    {getCommissionTier(vendor.sales_count || 0).amount}€
                  </span>
                </td>
                <td className="px-6 py-4 text-[#00FF88] font-bold">{vendor.balance_available?.toFixed(2) || '0.00'}€</td>
                <td className="px-6 py-4 text-[#00FFFF] font-bold">{vendor.balance_in_withdrawal?.toFixed(2) || '0.00'}€</td>
                <td className="px-6 py-4 text-[#FFB800] font-bold">{vendor.balance_pending?.toFixed(2) || '0.00'}€</td>
                <td className="px-6 py-4">
                  <div className="flex flex-col space-y-1">
                    {vendor.is_approved ? (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00FF88]/10 text-[#00FF88] w-fit">
                        Approuvé
                      </span>
                    ) : (
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#FFB800]/10 text-[#FFB800] w-fit">
                        En attente
                      </span>
                    )}
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold w-fit ${
                      vendor.is_active ? 'bg-[#00FFFF]/10 text-[#00FFFF]' : 'bg-[#FF3B5C]/10 text-[#FF3B5C]'
                    }`}>
                      {vendor.is_active ? 'Actif' : 'Désactivé'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-2">
                    {!vendor.is_approved && (
                      <button
                        onClick={() => approveVendor(vendor.id)}
                        disabled={actionLoading === vendor.id}
                        className="text-[#00FF88] hover:text-[#00FF88]/80 p-1"
                        title="Approuver"
                        data-testid={`approve-vendor-${index}`}
                      >
                        <CheckCircle className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => openBalanceModal(vendor)}
                      className="text-[#00FFFF] hover:text-[#00E5E5] p-1"
                      title="Modifier le solde"
                      data-testid={`edit-balance-${index}`}
                    >
                      <Euro className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => viewVendorClients(vendor)}
                      className="text-[#B8B8BA] hover:text-white p-1"
                      title="Voir détails"
                      data-testid={`view-vendor-${index}`}
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => toggleVendorStatus(vendor.id)}
                      disabled={actionLoading === vendor.id}
                      className={`p-1 ${vendor.is_active ? 'text-[#FFB800] hover:text-[#FFA000]' : 'text-[#00FF88] hover:text-[#00E577]'}`}
                      title={vendor.is_active ? 'Désactiver' : 'Activer'}
                      data-testid={`toggle-vendor-${index}`}
                    >
                      <Power className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(vendor)}
                      className="text-[#FF3B5C] hover:text-[#FF2244] p-1"
                      title="Supprimer"
                      data-testid={`delete-vendor-${index}`}
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showDetails && selectedVendor && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowDetails(false)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border cyber-glow max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto"
            data-testid="vendor-details-modal"
          >
            <h2 className="text-3xl font-bold text-white mb-6">Détails Vendeur - {selectedVendor.full_name}</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-[#222226] p-6 rounded-lg text-center">
                <div className="text-2xl font-bold text-white mb-1">{selectedVendor.sales_count || 0}</div>
                <div className="text-[#B8B8BA] text-sm">Ventes totales</div>
              </div>
              <div 
                className="bg-[#222226] p-6 rounded-lg text-center cursor-pointer hover:bg-[#2A2A2D] transition-colors group relative"
                onClick={() => openCommissionModal(selectedVendor)}
              >
                <div className={`text-2xl font-bold mb-1 ${getCommissionTier(selectedVendor.sales_count || 0).color}`}>
                  {getCommissionTier(selectedVendor.sales_count || 0).amount}€
                </div>
                <div className="text-[#B8B8BA] text-sm">Commission actuelle</div>
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Edit className="h-4 w-4 text-[#00FFFF]" />
                </div>
                <div className="text-[#6B6B6E] text-xs mt-1">{getCommissionTier(selectedVendor.sales_count || 0).tier}</div>
              </div>
              <div className="bg-[#222226] p-6 rounded-lg text-center">
                <div className="text-2xl font-bold text-[#00FF88] mb-1">{selectedVendor.balance?.toFixed(2)}€</div>
                <div className="text-[#B8B8BA] text-sm">Disponible</div>
              </div>
              <div className="bg-[#222226] p-6 rounded-lg text-center">
                <div className="text-2xl font-bold text-[#FFB800] mb-1">{selectedVendor.pending_balance?.toFixed(2)}€</div>
                <div className="text-[#B8B8BA] text-sm">En attente</div>
              </div>
            </div>

            <h3 className="text-xl font-bold text-white mb-4">Clients rattachés ({vendorClients.length})</h3>
            {vendorClients.length === 0 ? (
              <div className="bg-[#222226] p-8 rounded-lg text-center">
                <p className="text-[#B8B8BA]">Aucun client pour ce vendeur</p>
              </div>
            ) : (
              <div className="bg-[#222226] rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-[#1A1A1D]">
                    <tr>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] text-sm">Client</th>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] text-sm">Email</th>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] text-sm">Date inscription</th>
                      <th className="px-4 py-3 text-left text-[#B8B8BA] text-sm">Abonnement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendorClients.map((client, i) => (
                      <tr key={i} className="border-t border-[#3A3A3D]">
                        <td className="px-4 py-3 text-white">{client.name}</td>
                        <td className="px-4 py-3 text-[#B8B8BA] text-sm">{client.email}</td>
                        <td className="px-4 py-3 text-[#B8B8BA] text-sm">{formatDate(client.created_at)}</td>
                        <td className="px-4 py-3">
                          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#00FF88]/10 text-[#00FF88]">
                            {client.status || 'active'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowDeleteConfirm(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-md w-full mx-4"
            data-testid="delete-vendor-confirm-modal"
          >
            <div className="text-center">
              <div className="inline-block p-4 bg-[#FF3B5C]/10 rounded-full mb-4">
                <AlertTriangle className="h-12 w-12 text-[#FF3B5C]" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Supprimer ce vendeur ?</h3>
              <p className="text-[#B8B8BA] mb-6">
                Cette action est <span className="text-[#FF3B5C] font-bold">irréversible</span>. 
                Le compte de <span className="text-white font-semibold">{showDeleteConfirm.full_name}</span> et toutes ses données seront supprimés définitivement.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(null)}
                  className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D] transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={() => deleteVendor(showDeleteConfirm.id)}
                  disabled={actionLoading === showDeleteConfirm.id}
                  className="flex-1 bg-[#FF3B5C] text-white py-3 rounded-lg hover:bg-[#FF2244] transition-colors disabled:opacity-50"
                  data-testid="confirm-delete-vendor-btn"
                >
                  {actionLoading === showDeleteConfirm.id ? 'Suppression...' : 'Supprimer'}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Balance Edit Modal */}
      {showBalanceModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowBalanceModal(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-md w-full mx-4"
            data-testid="balance-modal"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Modifier le solde</h3>
              <button onClick={() => setShowBalanceModal(null)} className="text-[#B8B8BA] hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-[#222226] p-4 rounded-lg">
                <p className="text-[#B8B8BA] text-sm">Vendeur</p>
                <p className="text-white font-semibold">{showBalanceModal.full_name}</p>
                <p className="text-[#6B6B6E] text-sm">{showBalanceModal.email}</p>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-[#222226] p-4 rounded-lg">
                  <p className="text-[#B8B8BA] text-sm">Solde disponible</p>
                  <p className="text-[#00FF88] font-bold text-xl">{showBalanceModal.balance_available?.toFixed(2) || '0.00'}€</p>
                </div>
                <div className="bg-[#222226] p-4 rounded-lg">
                  <p className="text-[#B8B8BA] text-sm">En cours de retrait</p>
                  <p className="text-[#00FFFF] font-bold text-xl">{showBalanceModal.balance_in_withdrawal?.toFixed(2) || '0.00'}€</p>
                </div>
                <div className="bg-[#222226] p-4 rounded-lg">
                  <p className="text-[#B8B8BA] text-sm">Solde en attente</p>
                  <p className="text-[#FFB800] font-bold text-xl">{showBalanceModal.balance_pending?.toFixed(2) || '0.00'}€</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-[#E8E8E8] mb-2">Nouveau solde disponible (€)</label>
                  <input
                    type="number"
                    value={balanceFields.balance_available}
                    onChange={(e) => setBalanceFields({ ...balanceFields, balance_available: e.target.value })}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-[#00FFFF] font-bold text-xl focus:border-[#00FFFF] focus:outline-none"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-[#E8E8E8] mb-2">Nouveau en cours de retrait (€)</label>
                  <input
                    type="number"
                    value={balanceFields.balance_in_withdrawal}
                    onChange={(e) => setBalanceFields({ ...balanceFields, balance_in_withdrawal: e.target.value })}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-[#00FFFF] font-bold text-xl focus:border-[#00FFFF] focus:outline-none"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-[#E8E8E8] mb-2">Nouveau solde en attente (€)</label>
                  <input
                    type="number"
                    value={balanceFields.balance_pending}
                    onChange={(e) => setBalanceFields({ ...balanceFields, balance_pending: e.target.value })}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-[#00FFFF] font-bold text-xl focus:border-[#00FFFF] focus:outline-none"
                    step="0.01"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#E8E8E8] mb-2">Raison (optionnel)</label>
                <input
                  type="text"
                  value={balanceReason}
                  onChange={(e) => setBalanceReason(e.target.value)}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none"
                  placeholder="Ex: Correction erreur, bonus..."
                />
              </div>

              {balanceSuccess && (
                <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 text-[#00FF88] p-3 rounded-lg flex items-center space-x-2">
                  <CheckCircle className="h-5 w-5" />
                  <span>{balanceSuccess}</span>
                </div>
              )}

              <div className="flex space-x-4 pt-2">
                <button
                  onClick={() => setShowBalanceModal(null)}
                  className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D] transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={updateVendorBalance}
                  disabled={balanceLoading}
                  className="flex-1 btn-primary py-3 disabled:opacity-50"
                >
                  {balanceLoading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      Mise à jour...
                    </span>
                  ) : (
                    'Enregistrer'
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Commission Edit Modal */}
      {showCommissionModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setShowCommissionModal(null)}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-[#1A1A1D] p-8 rounded-lg metallic-border max-w-md w-full mx-4"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Modifier la commission</h3>
              <button onClick={() => setShowCommissionModal(null)} className="text-[#B8B8BA] hover:text-white">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {commissionSuccess ? (
              <div className="bg-[#00FF88]/10 border border-[#00FF88]/30 p-6 rounded-lg text-center">
                <CheckCircle className="h-16 w-16 text-[#00FF88] mx-auto mb-4" />
                <p className="text-[#00FF88] font-semibold text-lg">{commissionSuccess}</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-[#222226] p-4 rounded-lg">
                  <p className="text-[#B8B8BA] text-sm">Vendeur</p>
                  <p className="text-white font-semibold">{showCommissionModal.full_name}</p>
                  <p className="text-[#6B6B6E] text-sm">{showCommissionModal.email}</p>
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#222226] p-4 rounded-lg">
                    <p className="text-[#B8B8BA] text-sm">Ventes totales</p>
                    <p className="text-white font-bold text-xl">{showCommissionModal.sales_count || 0}</p>
                  </div>
                  <div className="bg-[#222226] p-4 rounded-lg">
                    <p className="text-[#B8B8BA] text-sm">Palier suggéré</p>
                    <p className={`font-bold text-xl ${getCommissionTier(showCommissionModal.sales_count || 0).color}`}>
                      {getCommissionTier(showCommissionModal.sales_count || 0).amount}€
                    </p>
                    <p className="text-[#6B6B6E] text-xs mt-1">
                      {getCommissionTier(showCommissionModal.sales_count || 0).tier}
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-[#E8E8E8] mb-2">Commission par vente (€)</label>
                  <input
                    type="number"
                    value={newCommission}
                    onChange={(e) => setNewCommission(e.target.value)}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-[#00FFFF] font-bold text-xl focus:border-[#00FFFF] focus:outline-none"
                    step="0.01"
                    min="0"
                  />
                  <p className="text-[#6B6B6E] text-xs mt-1">
                    Cette commission s&apos;appliquera à toutes les futures ventes
                  </p>
                </div>

                <div>
                  <label className="block text-[#E8E8E8] mb-2">Raison de la modification (optionnel)</label>
                  <textarea
                    value={commissionReason}
                    onChange={(e) => setCommissionReason(e.target.value)}
                    className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg px-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none resize-none"
                    rows="3"
                    placeholder="Ex: Ajustement exceptionnel, négociation commerciale..."
                  />
                </div>

                <div className="flex space-x-4">
                  <button
                    onClick={() => setShowCommissionModal(null)}
                    className="flex-1 bg-[#3A3A3D] text-white py-3 rounded-lg hover:bg-[#4A4A4D] transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={updateVendorCommission}
                    disabled={commissionLoading}
                    className="flex-1 bg-[#00FFFF] text-[#0A0A0B] py-3 rounded-lg hover:bg-[#00CCCC] transition-colors font-semibold disabled:opacity-50 flex items-center justify-center"
                  >
                    {commissionLoading ? (
                      <>
                        <Loader2 className="animate-spin h-5 w-5 mr-2" />
                        Mise à jour...
                      </>
                    ) : (
                      'Mettre à jour'
                    )}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default VendorsManagement;